<script setup lang="ts">
import { computed, ref } from 'vue'
import type { IProductCardProps } from '../types'
import { getPolishGenitive } from '@/cms-ui/utils/polishGenitivie'
import { getLinkTarget } from '@/cms-ui/utils/linkTarget'
import { useDrawer } from '@/cms-ui/composables/useDrawer'
import { polishCurrencyTransformer } from '@/cms-ui/utils/polishCurrencyTransformer'
import { ECarouselDesktopWidth } from '@/ui/plushComponents/VCarousel/types'
import { VButtonVariants } from '@/ui/plushComponents/VButton/types'

const props = defineProps<IProductCardProps>()
const { openDrawer } = useDrawer()
const buttonHover = ref(false)

const installmentOptions = computed(() => {
    if (!props.product?.installmentPriceList) return []

    // Create a Map to store unique installmentNo values
    const uniqueInstallments = new Map()

    // Keep only first occurrence of each installmentNo
    props.product.installmentPriceList.forEach(installment => {
        if (!uniqueInstallments.has(installment.installmentNo)) {
            uniqueInstallments.set(installment.installmentNo, installment)
        }
    })

    // Convert Map values to array and sort by installmentNo
    return Array.from(uniqueInstallments.values())
        .sort((a, b) => (a.installmentNo || 0) - (b.installmentNo || 0))
        .map(installment => ({
            id: installment.installmentNo?.toString() || '',
            label: `${installment.installmentNo} ${getPolishGenitive(installment.installmentNo, {
                singular: 'rata',
                plural2to4: 'raty',
                plural5plus: 'rat',
            })}`,
            value: installment.installmentNo?.toString() || '',
        }))
})

const selectedInstallment = ref(
    (() => {
        if (props.product?.installmentPriceList?.length === 1) {
            return props.product.installmentPriceList[0].installmentNo?.toString()
        }
        return props.defaultInstallment
    })(),
)

const computedBadges = computed(() => {
    const badges = props.badges
    if (props.priceBadge && selectedInstallment.value) {
        const selectedInstallmentObject = props.product?.installmentPriceList?.find(
            installment => installment.installmentNo?.toString() === selectedInstallment.value,
        )
        if (
            selectedInstallmentObject?.beforePromotionPrice &&
            selectedInstallmentObject?.hardwarePrice &&
            selectedInstallmentObject?.beforePromotionPrice > selectedInstallmentObject?.hardwarePrice &&
            !badges?.find(item => item.label.includes('zł TANIEJ'))
        ) {
            badges?.unshift({
                label: `${polishCurrencyTransformer(
                    Math.round(
                        selectedInstallmentObject?.beforePromotionPrice - selectedInstallmentObject?.hardwarePrice,
                    ),
                )} TANIEJ`,
                visualVariant: props.priceBadge.visualVariant!,
            })
        }
    }

    return badges
})

const sortedPhotoList = computed(() => {
    return (props.product?.photoList ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
})

const hoverClasses = {
    [VButtonVariants.PRIMARY]: '!text-brand-accent',
    [VButtonVariants.SECONDARY]: '!bg-comp-ghost-hover',
    [VButtonVariants.ACCENT]: '!bg-comp-accent-hover',
    [VButtonVariants.PLAIN]: '!bg-comp-ghost-hover',
}

function inactive(): void {
    buttonHover.value = false
}
function hover(): void {
    buttonHover.value = true
}
</script>

<template>
    <VCarouselSlide
        class="light group col-span-4"
        :class="{
            'max-lg:!px-0': mobileLayout.toLowerCase() === ECarouselDesktopWidth.GRID,
            'lg:!px-0': desktopLayout.toLowerCase() === ECarouselDesktopWidth.GRID,
        }"
        @mouseenter="hover"
        @mouseleave="inactive"
    >
        <div
            class="bg-neutral-primary border-thin rounded-container-lg border-neutral-subtlest px-base lg:px-md text-neutral-primary col-span-4 flex h-full flex-col"
        >
            <div class="gap-xs mt-sm lg:mt-base flex h-[30px] lg:h-[26px]">
                <VBadge
                    v-for="(badge, index) in computedBadges"
                    :key="`badge_${index}`"
                    data-test="badge"
                    size="SMALL"
                    :icon="badge.icon"
                    :variant="badge.visualVariant"
                >
                    {{ badge.label }}
                </VBadge>
            </div>

            <h3 class="heading-xs line-clamp-2 h-[56px]">
                {{ product?.name }}
            </h3>

            <div class="">
                <BaseImage
                    v-if="product?.photoList?.length === 1"
                    class="duration-400 h-[192px] origin-center transition-transform ease-in-out group-hover:scale-105 md:h-[240px] lg:h-[256px]"
                    :image-data="{
                        original: {
                            links: {
                                site: {
                                    href: product?.photoList[0]?.url,
                                },
                            },
                        },
                    }"
                />
                <VCarousel
                    v-else
                    data-test="product-list-carousel"
                    carousel-aria-label="product-list"
                    :desktop-snap="true"
                    desktop-size="MEDIUM"
                    desktop-slides-per-view="1"
                    :loop="false"
                    :hover-zoom="true"
                >
                    <VCarouselSlide
                        v-for="(product, i) in sortedPhotoList"
                        :key="i"
                    >
                        <div class="flex justify-center">
                            <BaseImage
                                class="duration-400 h-[192px] origin-center transition-transform ease-in-out group-hover:scale-105 md:h-[240px] lg:h-[256px]"
                                :image-data="{
                                    original: {
                                        links: {
                                            site: {
                                                href: product?.url,
                                            },
                                        },
                                    },
                                }"
                            />
                        </div>
                    </VCarouselSlide>

                    <template #bottom>
                        <VCarouselPagination />
                    </template>
                </VCarousel>
            </div>

            <div class="flex grow flex-col">
                <div class="column gap-base">
                    <div
                        v-if="installmentSelector"
                        class="my-xs lg:my-base h-[46px]"
                    >
                        <VSelector
                            v-if="installmentOptions.length > 1 && installmentSelector"
                            v-model="selectedInstallment"
                            variant="compact"
                            :options="installmentOptions"
                            layout="grid"
                        />
                    </div>

                    <ProductListPartsPriceBox
                        :selected-installment="selectedInstallment"
                        :installments="product?.installmentPriceList"
                    />
                </div>

                <div class="grow" />

                <VButton
                    size="MEDIUM"
                    data-test="product-card-button"
                    :variant="productButton?.visualVariant"
                    :href="productButton?.action?.url"
                    target="_self"
                    class="my-base lg:my-md w-full"
                    :class="{
                        [hoverClasses[productButton?.visualVariant ?? VButtonVariants.PRIMARY]]: buttonHover,
                    }"
                    @click.stop="openDrawer(productButton?.action?.drawer, page)"
                >
                    {{ productButton?.label }}
                </VButton>
            </div>
        </div>
    </VCarouselSlide>
</template>
